import React, { useState, useEffect, useRef } from 'react';
import {
  Menu, X, Phone, MapPin, Clock,
  Instagram, Facebook, ChevronLeft, ChevronRight,
  Globe, Star, Scissors, Video, Mail, MessageSquare,
  Car, Smile, Heart, PawPrint, ArrowRight, Linkedin, Calendar
} from 'lucide-react';
import './index.css';

// --- DICTIONARY FOR TRANSLATIONS ---
const translations = {
  en: {
    home: "Home",
    services: "Services",
    team: "Our Team",
    gallery: "Gallery",
    contact: "Contact",
    bookNow: "Book Now",
    sitemap: "Sitemap",

    heroTitle: "Infinite Love & Care for Your Pet",
    heroSub: "Experience the ultimate pet-care facility in Ogden. From daycare to VIP grooming, we treat your pets like family.",
    statPets: "Happy Pets",
    statYears: "Years Experience",
    statServices: "Premium Services",

    servicesTitle: "Our Premium Services",
    servicesSub: "Everything your best friend needs in one place.",
    srv1Title: "Daycare & Boarding",
    srv1Desc: "Doggy daycare and overnight boarding services with report cards and live webcams.",
    srv2Title: "Grooming & Bath",
    srv2Desc: "Bath with haircut, special brushing for heavy shedders, and teeth brushing.",
    srv3Title: "Nail Spa & Canine Art",
    srv3Desc: "Nail painting, cutting, and filing. We also offer creative canine art and painting.",
    srv4Title: "Self-Serve Bath",
    srv4Desc: "Open for clients to bathe their own dogs with our professional guidance and tools.",
    srv5Title: "Pet Taxi",
    srv5Desc: "VIP Taxi - Coming Soon! We pick up and drop off your dog right at your doorstep.",
    srv6Title: "Health & Walks",
    srv6Desc: "Daily walks, exercise and professional gland expression.",

    teamTitle: "Meet Our Experts",
    teamSub: "Dedicated professionals providing the best care.",
    role1: "Master Groomer & Pet Stylist",
    role2: "Pet Care & Welfare Specialist",

    testTitle: "Happy Clients",
    testSub: "See what our furry friends and their owners say about us.",
    ownerOf: "Owner of",
    reviews: [
      { text: "Best groomers in town! My golden retriever always comes back smelling amazing and looking fresh.", author: "Sarah Jenkins", pet: "Max" },
      { text: "I love the daycare service. The webcams give me peace of mind while I'm at work.", author: "Mike T.", pet: "Bella" },
      { text: "They painted my poodle's nails and did some canine art! It was so cute.", author: "Jessica Wong", pet: "Chloe" },
      { text: "I used the self-serve bath. They provided all the tools and guided me. Great experience!", author: "David R.", pet: "Ghost" },
      { text: "Fantastic boarding facility. The report cards they send are adorable.", author: "Emily C.", pet: "Luna" }
    ],

    bookTitle: "Schedule an Appointment",
    bookName: "Your Name",
    bookPet: "Pet's Name",
    bookService: "Select Service",
    bookDate: "Start Date",
    bookEndDate: "End Date",
    bookMessage: "Additional Notes",
    bookSubmit: "Send Request via SMS / Email",
    bookSuccess: "Sending your request...",

    // Form Wizard Translations
    step1: "Tutor & Pet",
    step2: "Service",
    step3: "Time",
    step4: "Review",
    step5: "Payment",
    step6: "Done",

    chooseService: "Choose a service...",
    availableTimes: "Available Times",
    nextStep: "Next Step",
    back: "Back",
    reviewBooking: "Review Booking",

    petGuardian: "Pet Guardian Details",
    yourName: "Your Full Name",
    mobilePhone: "Mobile Phone (For SMS)",
    emailAddr: "Email Address",

    patientDetails: "Patient (Pet) Details",
    petsName: "Pet's Name",
    breed: "Breed",
    weight: "Weight (lbs)",
    age: "Age",
    allergies: "Medical Notes / Allergies (Required)",

    // Dynamic Pricing & Add-ons
    secondPetPromo: "Add a second pet for 15% OFF!",
    hasSecondPet: "Add Second Pet",
    btnYes: "Yes",
    btnNo: "No",
    tabSubscription: "Monthly Subscription",
    tabGrooming: "Grooming & Bath",
    tabWalkin: "Walk-in & Add-ons",
    subDesc: "We have a monthly package for Small dogs for just $120, Medium/Large $150. You can bring them as many times as needed throughout the month for any service.",
    subPrice: "from $120",
    groomType: "Select Type",
    typeFull: "Full Groom & Bath",
    typeBath: "Bath Only",
    hairLength: "Hair Length",
    hairShort: "Short Hair",
    hairLong: "Long Hair",
    petWeight: "Select Size/Weight",
    sizeS: "0.5 - 7 kg (1 - 15 lbs) - Small",
    sizeM: "9 - 18 kg (20 - 40 lbs) - Medium",
    sizeL: "Under 22.5 kg (Under 50 lbs) - Large",
    walkInBaseDesc: "Base Package (+$40): Ear cleaning, paw balm, face/paw/anal trim, nail trim, brushing, fragrance, toothbrushing.",
    walkInAddons: "Add-ons:",
    addonNails: "Nail Polish & Trim (+$15)",
    addonTeeth: "Teeth Brushing & Fresh Breath (+$10)",
    addonGlands: "Gland Expression (+$10)",
    addonEars: "Deep Ear Cleaning (+$10)",
    addonNailCut: "Nail Cut Only (+$7)",
    priceCustom: "Price Calculation",
    serviceIncluded: "Included",

    confirmBookingTitle: "Confirm Booking",
    confirmDesc1: "Please review your",
    confirmDesc2: "appointment details for",
    confirmDesc3: "at",
    petNameLabel: "Pet Name",
    ownerLabel: "Owner",
    serviceTotal: "Service Total (Est.)",
    bookAppointmentBtn: "Book Appointment",
    processing: "Processing...",
    cancelGoBack: "Cancel & go back",
    contactPrompt: "To schedule an appointment, contact the clinic directly:",
    contactCall: "Call",
    contactSMS: "Text (SMS)",
    contactEmail: "Email",

    successTitle: "Booking Confirmed!",
    successDesc: "Your appointment is locked in our calendar. We've sent a confirmation SMS to your phone and an email with all the details.",
    bookAnother: "Book another service",

    contactTitle: "Get in Touch",
    address: "Address",
    hours: "Opening Hours",
    hoursText: "Monday to Friday: 8:00 am - 7:00 pm\nSaturday: 10:00 am - 4:00 pm\nSunday: Closed",
    phone: "Phone",
    rights: "All rights reserved.",
    logoSubtitle: "Pet shop, daycare and boarding for dogs."
  },
  pt: {
    home: "Início",
    services: "Serviços",
    team: "Equipa",
    gallery: "Galeria",
    contact: "Contacto",
    bookNow: "Agendar",
    sitemap: "Mapa do Site",

    heroTitle: "Amor e Cuidado Infinito para o seu Pet",
    heroSub: "Conheça o melhor centro de cuidados em Ogden. Da creche ao banho VIP, tratamos o seu pet como família.",
    statPets: "Pets Felizes",
    statYears: "Anos de Experiência",
    statServices: "Serviços Premium",

    servicesTitle: "Os Nossos Serviços Premium",
    servicesSub: "Tudo o que o seu melhor amigo precisa num só lugar.",
    srv1Title: "Creche e Hospedagem",
    srv1Desc: "Creche canina e hospedagem noturna com boletins de avaliação e webcams ao vivo.",
    srv2Title: "Banho e Tosa",
    srv2Desc: "Banho com tosa, escovagem especial para cães que soltam muito pelo e escovagem dentária.",
    srv3Title: "Spa de Unhas e Arte",
    srv3Desc: "Pintura, corte e lixamento de unhas. Também oferecemos arte e pintura canina criativa.",
    srv4Title: "Banho Autoatendimento",
    srv4Desc: "Aberto para os clientes darem banho aos seus cães com a nossa orientação profissional e ferramentas.",
    srv5Title: "Táxi Pet",
    srv5Desc: "VIP Taxi - Em Breve! Buscamos e levamos o seu cão no conforto da sua casa.",
    srv6Title: "Saúde e Passeios",
    srv6Desc: "Passeios diários, exercícios e expressão profissional de glândula.",

    teamTitle: "Os Nossos Especialistas",
    teamSub: "Profissionais dedicados a oferecer o melhor cuidado.",
    role1: "Master Groomer & Estilista Pet",
    role2: "Pet Care & Welfare Specialist",

    testTitle: "Clientes Satisfeitos",
    testSub: "Veja o que os nossos amigos peludos e os seus donos dizem sobre nós.",
    ownerOf: "Tutor(a) do(a)",
    reviews: [
      { text: "Os melhores profissionais da cidade! O meu golden retriever volta sempre cheiroso e lindo.", author: "Sarah Jenkins", pet: "Max" },
      { text: "Adoro o serviço de creche. As webcams dão-me paz de espírito enquanto estou no trabalho.", author: "Mike T.", pet: "Bella" },
      { text: "Pintaram as unhas da minha caniche e fizeram arte canina! Ficou tão fofo.", author: "Jessica Wong", pet: "Chloe" },
      { text: "Usei o banho de autoatendimento. Eles forneceram as ferramentas e guiaram-me. Ótima experiência!", author: "David R.", pet: "Ghost" },
      { text: "Instalações fantásticas para hospedagem. Os boletins que enviam são adoráveis.", author: "Emily C.", pet: "Luna" }
    ],

    bookTitle: "Agende um Horário",
    bookName: "O Seu Nome",
    bookPet: "Nome do Pet",
    bookService: "Selecione o Serviço",
    bookDate: "Data de Início",
    bookEndDate: "Data de Término",
    bookMessage: "Observações Adicionais",
    bookSubmit: "Enviar Pedido via SMS / E-mail",
    bookSuccess: "A enviar o seu pedido...",

    // Form Wizard Translations
    step1: "Tutor e Pet",
    step2: "Serviço",
    step3: "Horário",
    step4: "Revisão",
    step5: "Pagamento",
    step6: "Finalizado",

    chooseService: "Escolha um serviço...",
    availableTimes: "Horários Disponíveis",
    nextStep: "Próximo Passo",
    back: "Voltar",
    reviewBooking: "Revisar Agendamento",

    petGuardian: "Detalhes do Tutor",
    yourName: "Seu Nome Completo",
    mobilePhone: "Telemóvel (Para SMS)",
    emailAddr: "Endereço de E-mail",

    patientDetails: "Detalhes do Paciente (Pet)",
    petsName: "Nome do Pet",
    breed: "Raça",
    weight: "Peso (kg)",
    age: "Idade",
    allergies: "Notas Médicas / Alergias (Obrigatório)",

    // Dynamic Pricing & Add-ons
    secondPetPromo: "Adicione um segundo pet com 15% de DESCONTO!",
    hasSecondPet: "Adicionar Segundo Pet",
    btnYes: "Sim",
    btnNo: "Não",
    tabSubscription: "Assinatura Mensal",
    tabGrooming: "Banho & Tosa",
    tabWalkin: "Sem Hora Marcada (Walk-in)",
    subDesc: "Temos um pacote mensal para cães pequenos por apenas US$120, médio e grande US$150. Você pode trazê-los quantas vezes precisar ao longo do mês para qualquer serviço.",
    subPrice: "a partir de US$120",
    groomType: "Tipo de Serviço",
    typeFull: "Banho e Tosa Completa",
    typeBath: "Apenas Banho",
    hairLength: "Tamanho do Pelo",
    hairShort: "Pelo Curto",
    hairLong: "Pelo Longo",
    petWeight: "Peso / Tamanho",
    sizeS: "0.5 - 7 kg (Pequeno)",
    sizeM: "9 - 18 kg (Médio)",
    sizeL: "Até 22.5 kg (Grande)",
    walkInBaseDesc: "Pacote Base (+$40): Limpeza de ouvidos, bálsamo patas, aparar rosto/patas/anal, unhas, escovação, spray, hálito fresco.",
    walkInAddons: "Adicionais:",
    addonNails: "Corte e Polimento de Unhas (+$15)",
    addonTeeth: "Escovação e Hálito Fresco (+$10)",
    addonGlands: "Expressão de Glândulas (+$10)",
    addonEars: "Limpeza Profunda de Ouvidos (+$10)",
    addonNailCut: "Apenas Corte de Unhas (+$7)",
    priceCustom: "Cálculo de Preço",
    serviceIncluded: "Incluído",

    confirmBookingTitle: "Confirmar Agendamento",
    confirmDesc1: "Por favor, revise os detalhes do seu agendamento de",
    confirmDesc2: "para o dia",
    confirmDesc3: "às",
    petNameLabel: "Nome do Pet",
    ownerLabel: "Tutor",
    serviceTotal: "Total do Serviço (Est.)",
    bookAppointmentBtn: "Agendar Horário",
    processing: "A processar...",
    cancelGoBack: "Cancelar e voltar",
    contactPrompt: "Para agendar um horário, fale diretamente com a clínica:",
    contactCall: "Ligar",
    contactSMS: "SMS",
    contactEmail: "E-mail",

    successTitle: "Reserva Confirmada!",
    successDesc: "O seu horário está reservado no nosso calendário. Enviámos um SMS de confirmação para o seu telemóvel e um e-mail com todos os detalhes.",
    bookAnother: "Agendar outro serviço",

    contactTitle: "Entre em Contacto",
    address: "Endereço",
    hours: "Horário de Funcionamento",
    hoursText: "Segunda a Sexta: 08:00 - 19:00\nSábado: 10:00 - 16:00\nDomingo: Fechado",
    phone: "Telefone",
    rights: "Todos os direitos reservados.",
    logoSubtitle: "Pet shop, creche e hotel para cães."
  },
  es: {
    home: "Inicio",
    services: "Servicios",
    team: "Equipo",
    gallery: "Galería",
    contact: "Contacto",
    bookNow: "Reservar",
    sitemap: "Mapa del Sitio",

    heroTitle: "Amor y Cuidado Infinito para tu Mascota",
    heroSub: "Descubre el mejor centro de cuidado en Ogden. Desde la guardería hasta el baño VIP.",
    statPets: "Mascotas Felices",
    statYears: "Años de Experiencia",
    statServices: "Servicios Premium",

    servicesTitle: "Nuestros Servicios Premium",
    servicesSub: "Todo lo que tu mejor amigo necesita en un solo lugar.",
    srv1Title: "Guardería y Alojamiento",
    srv1Desc: "Guardería canina y alojamiento nocturno con boletines y cámaras web en vivo.",
    srv2Title: "Baño y Corte",
    srv2Desc: "Baño con corte de pelo, cepillado especial para perros que sueltan mucho pelo y cepillado dental.",
    srv3Title: "Spa de Uñas y Arte",
    srv3Desc: "Pintura, corte y limado de uñas. También ofrecemos arte y pintura canina creativa.",
    srv4Title: "Autoservicio de Baño",
    srv4Desc: "Abierto para que los clientes bañen a sus perros con nuestra orientación profesional.",
    srv5Title: "Taxi de Mascotas",
    srv5Desc: "VIP Taxi - ¡Próximamente! Recogemos y entregamos a su perro en la comodidad de su hogar.",
    srv6Title: "Salud y Paseos",
    srv6Desc: "Paseos diarios, ejercicio y expresión profesional de glándula.",

    teamTitle: "Nuestros Expertos",
    teamSub: "Profesionales dedicados a brindar el mejor cuidado.",
    role1: "Master Groomer y Estilista de Mascotas",
    role2: "Pet Care & Welfare Specialist",

    testTitle: "Clientes Felices",
    testSub: "Mira lo que dicen nuestros amigos peludos y sus dueños.",
    ownerOf: "Dueña de",
    reviews: [
      { text: "¡Los mejores peluqueros de la ciudad! Mi golden retriever siempre vuelve oliendo increíble.", author: "Sarah Jenkins", pet: "Max" },
      { text: "Me encanta el servicio de guardería. Las cámaras web me dan tranquilidad mientras trabajo.", author: "Mike T.", pet: "Bella" },
      { text: "¡Le pintaron las uñas a mi caniche e hicieron arte canino! Fue muy lindo.", author: "Jessica Wong", pet: "Chloe" },
      { text: "Usé el baño de autoservicio. Me proporcionaron todas las herramientas y me guiaron.", author: "David R.", pet: "Ghost" },
      { text: "Fantásticas instalaciones de alojamiento. Los boletines que envían son adorables.", author: "Emily C.", pet: "Luna" }
    ],

    bookTitle: "Programar una Cita",
    bookName: "Tu Nombre",
    bookPet: "Nombre de la Mascota",
    bookService: "Seleccionar Servicio",
    bookDate: "Fecha de Inicio",
    bookEndDate: "Fecha de Finalización",
    bookMessage: "Notas Adicionales",
    bookSubmit: "Enviar Reserva por SMS / Correo",
    bookSuccess: "Enviando su solicitud...",

    // Form Wizard Translations
    step1: "Tutor y Mascota",
    step2: "Servicio",
    step3: "Horario",
    step4: "Revisión",
    step5: "Pago",
    step6: "Hecho",

    chooseService: "Elige un servicio...",
    availableTimes: "Horarios Disponibles",
    nextStep: "Siguiente Paso",
    back: "Atrás",
    reviewBooking: "Revisar Reserva",

    petGuardian: "Detalles del Tutor",
    yourName: "Tu Nombre Completo",
    mobilePhone: "Teléfono Móvil (Para SMS)",
    emailAddr: "Correo Electrónico",

    patientDetails: "Detalles del Paciente (Mascota)",
    petsName: "Nombre de la Mascota",
    breed: "Raza",
    weight: "Peso (lbs)",
    age: "Edad",
    allergies: "Notas Médicas / Alergias (Obligatorio)",

    // Dynamic Pricing & Add-ons
    secondPetPromo: "¡Añade una segunda mascota con 15% de DESCUENTO!",
    hasSecondPet: "Añadir Segunda Mascota",
    btnYes: "Sí",
    btnNo: "No",
    tabSubscription: "Suscripción Mensual",
    tabGrooming: "Baño y Corte",
    tabWalkin: "Sin Cita (Walk-in)",
    subDesc: "Tenemos un paquete mensual para perros pequeños por solo $120, medianos/grandes $150. ¡Puedes traerlos tantas veces como necesites durante el mes para cualquier servicio!",
    subPrice: "desde $120",
    groomType: "Tipo de Servicio",
    typeFull: "Baño y Corte Completo",
    typeBath: "Solo Baño",
    hairLength: "Longitud del Pelo",
    hairShort: "Pelo Corto",
    hairLong: "Pelo Largo",
    petWeight: "Peso / Tamaño",
    sizeS: "0.5 - 7 kg (Pequeño)",
    sizeM: "9 - 18 kg (Mediano)",
    sizeL: "Hasta 22.5 kg (Grande)",
    walkInBaseDesc: "Paquete Base (+$40): Limpieza oídos, bálsamo patas, recorte cara/patas/anal, uñas, cepillado, spray, aliento fresco.",
    walkInAddons: "Adicionales:",
    addonNails: "Corte y Pulido de Uñas (+$15)",
    addonTeeth: "Cepillado y Aliento Fresco (+$10)",
    addonGlands: "Expresión de Glándulas (+$10)",
    addonEars: "Limpieza Profunda de Oídos (+$10)",
    addonNailCut: "Solo Corte de Uñas (+$7)",
    priceCustom: "Cálculo de Precio",
    serviceIncluded: "Incluido",

    confirmBookingTitle: "Confirmar Reserva",
    confirmDesc1: "Por favor, revisa los detalles de tu cita de",
    confirmDesc2: "para el",
    confirmDesc3: "a las",
    petNameLabel: "Mascota",
    ownerLabel: "Tutor",
    serviceTotal: "Total del Servicio (Est.)",
    bookAppointmentBtn: "Agendar Cita",
    processing: "Procesando...",
    cancelGoBack: "Cancelar y volver",
    contactPrompt: "Para programar una cita, contacta directamente con la clínica:",
    contactCall: "Llamar",
    contactSMS: "SMS",
    contactEmail: "Correo",

    successTitle: "¡Reserva Confirmada!",
    successDesc: "Tu cita está asegurada en nuestro calendario. Hemos enviado un SMS de confirmación a tu teléfono y un correo electrónico con los detalles.",
    bookAnother: "Reservar otro servicio",

    contactTitle: "Contáctanos",
    address: "Dirección",
    hours: "Horario de Atención",
    hoursText: "Lunes a Viernes: 8:00 am - 7:00 pm\nSábado: 10:00 am - 4:00 pm\nDomingo: Cerrado",
    phone: "Teléfono",
    rights: "Todos los derechos reservados.",
    logoSubtitle: "Pet shop, guardería y alojamiento para perros."
  }
};

const CALENDLY_URL = "https://calendly.com/douglasmidiacerta";
const BRAND_GRADIENT = "bg-[linear-gradient(to_right,#8e2634,#1e3b70,#f37821,#542468)]";
const TEXT_GRADIENT = `${BRAND_GRADIENT} bg-clip-text text-transparent`;

const useScrollReveal = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => {
      if (ref.current) observer.unobserve(ref.current);
    };
  }, [threshold]);

  return [ref, isVisible];
};

// --- LOGO COMPONENT RECREATING THE IMAGE ---
function BrandLogo({ t }) {
  return (
    <div className="flex flex-col items-center justify-center cursor-pointer hover:scale-105 transition-transform duration-300">
      {/* Text Area */}
      <div className="flex flex-col items-center">
        <div className="flex items-baseline gap-[6px]">
          <span className={`text-3xl font-black italic bg-[linear-gradient(to_right,#8e2634,#1e3b70)] bg-clip-text text-transparent tracking-tight`}>
            ESO
          </span>
          <span className={`text-3xl font-black italic bg-[linear-gradient(to_right,#1e3b70,#f37821,#542468)] bg-clip-text text-transparent lowercase tracking-tight`}>
            woof spa
          </span>
        </div>
        <span className="text-[0.65rem] font-medium text-[#542468] italic tracking-wider -mt-1">
          {t.logoSubtitle}
        </span>
      </div>
    </div>
  );
}

export default function App() {
  const [lang, setLang] = useState('en');
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const t = translations[lang] || translations['en'];

  // Função para fazer o scroll suave até a área de booking
  const scrollToBooking = () => {
    document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  useEffect(() => {
    const browserLang = navigator.language || navigator.userLanguage || 'en';
    if (browserLang.toLowerCase().startsWith('pt')) {
      setLang('pt');
    } else if (browserLang.toLowerCase().startsWith('es')) {
      setLang('es');
    } else {
      setLang('en');
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const changeLanguage = (newLang) => {
    setLang(newLang);
    setMobileMenuOpen(false);
  };

  return (
    <div className="font-sans text-gray-800 bg-gray-50 overflow-x-hidden">

      {/* --- NAVBAR --- */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md py-2' : 'bg-white/95 backdrop-blur-md py-4'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">

          <BrandLogo t={t} />

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            <a href="#home" className="font-bold text-[#1e3b70] hover:text-[#f37821] transition-colors">{t.home}</a>
            <a href="#services" className="font-bold text-[#1e3b70] hover:text-[#f37821] transition-colors">{t.services}</a>
            <a href="#team" className="font-bold text-[#1e3b70] hover:text-[#f37821] transition-colors">{t.team}</a>
            <a href="#gallery" className="font-bold text-[#1e3b70] hover:text-[#f37821] transition-colors">{t.gallery}</a>
            <a href="#contact" className="font-bold text-[#1e3b70] hover:text-[#f37821] transition-colors">{t.contact}</a>

            <div className="flex items-center gap-2 bg-gray-100 rounded-full px-3 py-1">
              <Globe size={16} className="text-[#1e3b70]" />
              <select
                value={lang}
                onChange={(e) => changeLanguage(e.target.value)}
                className="bg-transparent text-sm font-bold text-[#1e3b70] focus:outline-none cursor-pointer"
              >
                <option value="en">EN</option>
                <option value="pt">PT</option>
                <option value="es">ES</option>
              </select>
            </div>

            <button
              onClick={scrollToBooking}
              className="bg-[#1e3b70] text-white px-7 py-2.5 rounded-full font-bold shadow-lg shadow-[#1e3b70]/20 transition-transform hover:scale-105"
            >
              {t.bookNow}
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-4">
            <div className="flex items-center gap-1 bg-gray-100 rounded-full px-2 py-1">
              <Globe size={14} className="text-[#1e3b70]" />
              <select value={lang} onChange={(e) => changeLanguage(e.target.value)} className="bg-transparent font-bold text-[#1e3b70] text-xs focus:outline-none">
                <option value="en">EN</option>
                <option value="pt">PT</option>
                <option value="es">ES</option>
              </select>
            </div>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-[#1e3b70]">
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* Mobile Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 w-full bg-white shadow-xl py-4 flex flex-col items-center space-y-4 border-t border-gray-100">
            <a href="#home" onClick={() => setMobileMenuOpen(false)} className="text-lg font-bold text-[#1e3b70] w-full text-center py-2 hover:bg-gray-50">{t.home}</a>
            <a href="#services" onClick={() => setMobileMenuOpen(false)} className="text-lg font-bold text-[#1e3b70] w-full text-center py-2 hover:bg-gray-50">{t.services}</a>
            <a href="#team" onClick={() => setMobileMenuOpen(false)} className="text-lg font-bold text-[#1e3b70] w-full text-center py-2 hover:bg-gray-50">{t.team}</a>
            <a href="#gallery" onClick={() => setMobileMenuOpen(false)} className="text-lg font-bold text-[#1e3b70] w-full text-center py-2 hover:bg-gray-50">{t.gallery}</a>
            <a href="#contact" onClick={() => setMobileMenuOpen(false)} className="text-lg font-bold text-[#1e3b70] w-full text-center py-2 hover:bg-gray-50">{t.contact}</a>
            <button
              onClick={scrollToBooking}
              className="bg-[#1e3b70] text-white px-8 py-3 rounded-full font-bold w-10/12 text-center shadow-lg"
            >
              {t.bookNow}
            </button>
          </div>
        )}
      </nav>

      {/* Calendly removido */}

      {/* --- HERO SECTION --- */}
      <section id="home" className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 flex items-center justify-center min-h-[90vh] bg-[#1e3b70]">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1548199973-03cce0bbc87b?auto=format&fit=crop&q=80&w=2000"
            alt="Happy dogs running"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1e3b70]/95 via-[#1e3b70]/80 to-[#542468]/60"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-start w-full">
          <div className="max-w-2xl animate-[fadeIn_1s_ease-out]">

            <div className="relative inline-block mb-6 mt-4">
              <PawPrint className="absolute -top-8 -left-6 text-[#8e2634] opacity-80 animate-paw" size={36} />
              <h1 className="text-5xl lg:text-7xl font-extrabold text-white leading-tight drop-shadow-md">
                {t.heroTitle}
              </h1>
              <Heart className="absolute -bottom-6 -right-6 text-[#f37821] opacity-90 animate-heart" size={32} />
            </div>

            <p className="text-xl text-blue-50/90 mb-10 leading-relaxed max-w-lg mt-6">
              {t.heroSub}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={scrollToBooking}
                className="bg-[#1e3b70] text-white px-8 py-4 rounded-full font-bold text-lg text-center shadow-[0_0_30px_rgba(30,59,112,0.3)] transition-all hover:scale-105 flex items-center justify-center gap-2"
              >
                {t.bookNow} <ArrowRight size={20} />
              </button>
              <a href="#services" className="bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/30 text-white px-8 py-4 rounded-full font-bold text-lg text-center transition-all">
                {t.services}
              </a>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mt-16 pt-8 border-t border-white/20">
              <div>
                <p className="text-3xl font-extrabold text-white mb-1">1000+</p>
                <p className="text-sm text-blue-100 font-medium">{t.statPets}</p>
              </div>
              <div>
                <p className="text-3xl font-extrabold text-[#f37821] mb-1">10+</p>
                <p className="text-sm text-blue-100 font-medium">{t.statYears}</p>
              </div>
              <div>
                <p className="text-3xl font-extrabold text-white mb-1">15+</p>
                <p className="text-sm text-blue-100 font-medium">{t.statServices}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- SERVICES SECTION --- */}
      <section id="services" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className={`text-4xl md:text-5xl font-black ${TEXT_GRADIENT} mb-4 pb-2`}>
              {t.servicesTitle}
            </h2>
            <p className="text-lg text-[#1e3b70] font-medium">{t.servicesSub}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <ServiceCard icon={<Video className="text-[#8e2634]" size={32} />} title={t.srv1Title} desc={t.srv1Desc} delay="0" />
            <ServiceCard icon={<Scissors className="text-[#1e3b70]" size={32} />} title={t.srv2Title} desc={t.srv2Desc} delay="100" />
            <ServiceCard icon={<Star className="text-[#f37821]" size={32} />} title={t.srv3Title} desc={t.srv3Desc} delay="200" />
            <ServiceCard icon={<Smile className="text-[#542468]" size={32} />} title={t.srv4Title} desc={t.srv4Desc} delay="300" />
            <ServiceCard icon={<Car className="text-[#8e2634]" size={32} />} title={t.srv5Title} desc={t.srv5Desc} delay="400" />
            <ServiceCard icon={<Heart className="text-[#f37821]" size={32} />} title={t.srv6Title} desc={t.srv6Desc} delay="500" />
          </div>
        </div>
      </section>

      {/* --- TEAM SECTION --- */}
      <section id="team" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className={`text-4xl md:text-5xl font-black ${TEXT_GRADIENT} mb-4 pb-2`}>
              {t.teamTitle}
            </h2>
            <p className="text-lg text-[#1e3b70] font-medium">{t.teamSub}</p>
          </div>

          {/* Updated grid to center the 2 members */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-4xl mx-auto">
            <TeamMember
              name="Edna Oliveira"
              role={t.role1}
              img="/edna.png"
            />
            <TeamMember
              name="Samylla Pereira"
              role={t.role2}
              img="/samylla.png"
            />
          </div>
        </div>
      </section>

      {/* --- TESTIMONIALS --- */}
      <TestimonialCarousel t={t} />

      {/* --- GALLERY SECTION --- */}
      <section id="gallery" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className={`text-4xl md:text-5xl font-black text-center ${TEXT_GRADIENT} mb-12 pb-2`}>
            {t.gallery}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <img src="https://images.unsplash.com/photo-1517849845537-4d257902454a?auto=format&fit=crop&q=80&w=600" alt="Dog" className="rounded-2xl object-cover h-48 w-full hover:scale-105 transition-transform duration-500 cursor-pointer shadow-md" />
            <img src="https://images.unsplash.com/photo-1583511655857-d19b40a7a54e?auto=format&fit=crop&q=80&w=600" alt="Dog" className="rounded-2xl object-cover h-48 w-full hover:scale-105 transition-transform duration-500 cursor-pointer shadow-md" />
            <img src="https://images.unsplash.com/photo-1537151625747-768eb6cf92b2?auto=format&fit=crop&q=80&w=600" alt="Dog" className="rounded-2xl object-cover h-48 w-full hover:scale-105 transition-transform duration-500 cursor-pointer shadow-md md:col-span-2" />
            <img src="https://images.unsplash.com/photo-1601758176175-45914394491c?auto=format&fit=crop&q=80&w=800" alt="Dog grooming" className="rounded-2xl object-cover h-48 w-full hover:scale-105 transition-transform duration-500 cursor-pointer shadow-md md:col-span-2" />
            <img src="https://images.unsplash.com/photo-1530281700549-e82e7bf110d6?auto=format&fit=crop&q=80&w=600" alt="Dog" className="rounded-2xl object-cover h-48 w-full hover:scale-105 transition-transform duration-500 cursor-pointer shadow-md" />
            <img src="https://images.unsplash.com/photo-1544568100-847a948585b9?auto=format&fit=crop&q=80&w=600" alt="Dog" className="rounded-2xl object-cover h-48 w-full hover:scale-105 transition-transform duration-500 cursor-pointer shadow-md" />
          </div>
        </div>
      </section>

      {/* --- BOOKING FORM & CONTACT --- */}
      <section id="booking" className="py-24 bg-gray-50 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16">

          <div className="flex flex-col gap-8">
            <div className="bg-white p-6 md:p-8 rounded-3xl shadow-xl border border-gray-100 flex flex-col w-full h-full min-h-[600px]">
              <h3 className={`text-3xl font-black ${BRAND_GRADIENT} bg-clip-text text-transparent mb-6 pb-1`}>
                {t.bookTitle}
              </h3>

              <div className="flex-1 w-full flex flex-col">
                <BookingForm t={t} lang={lang} BRAND_GRADIENT={BRAND_GRADIENT} />
              </div>
            </div>
          </div>

          <div id="contact" className="flex flex-col justify-center space-y-8">
            <div>
              <h2 className={`text-4xl md:text-5xl font-black ${TEXT_GRADIENT} mb-6 pb-2`}>
                {t.contactTitle}
              </h2>
              <p className="text-[#1e3b70] font-medium text-lg mb-8">
                {lang === 'pt' ? 'Visite as nossas instalações ou ligue para tirar dúvidas. Estamos sempre prontos para receber você e o seu pet.' :
                  lang === 'es' ? 'Visite nuestras instalaciones o llame para hacer preguntas. Siempre estamos listos para recibirlo a usted y a su mascota.' :
                    'Visit our facility or give us a call. We are always ready to welcome you and your furry friend.'}
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="bg-[#1e3b70]/10 p-3 rounded-full text-[#1e3b70]">
                  <MapPin size={24} />
                </div>
                <div>
                  <h4 className="font-bold text-[#1e3b70]">{t.address}</h4>
                  <p className="text-gray-600">1949 N 400 E St<br />Ogden, UT 84414</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-[#f37821]/10 p-3 rounded-full text-[#f37821]">
                  <Phone size={24} />
                </div>
                <div>
                  <h4 className="font-bold text-[#1e3b70]">{t.phone}</h4>
                  <p className="text-gray-600 text-lg font-medium">(385) 326-9168</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-[#542468]/10 p-3 rounded-full text-[#542468]">
                  <Clock size={24} />
                </div>
                <div>
                  <h4 className="font-bold text-[#1e3b70]">{t.hours}</h4>
                  <p className="text-gray-600 whitespace-pre-line">{t.hoursText}</p>
                </div>
              </div>
            </div>

            <div className="w-full h-64 bg-gray-200 rounded-2xl overflow-hidden shadow-inner mt-6 border border-gray-200">
              <iframe
                title="Map Location"
                src="https://maps.google.com/maps?q=1949%20N%20400%20E%20St,%20Ogden,%20UT%2084414&t=&z=14&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* --- FOOTER COMPLETO --- */}
      <footer className="bg-[#1e3b70] text-blue-100 py-16 border-t border-[#1e3b70]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-4 gap-12">

          <div className="md:col-span-2">
            <div className="flex flex-col items-start mb-6 w-max bg-white/10 p-4 rounded-xl backdrop-blur-sm border border-white/20">
              <BrandLogo t={t} />
            </div>
            <p className="text-blue-200 mb-6 max-w-sm">
              {lang === 'pt' ? 'A proporcionar cuidado e amor de primeira classe aos seus animais de estimação em Ogden.' :
                lang === 'es' ? 'Brindando atención y amor de primera clase a sus mascotas en Ogden.' :
                  'Providing top-tier care and love for your pets in Ogden.'}
            </p>
            <div className="flex gap-4">
              <a href="https://www.instagram.com/eso.woofspa" target="_blank" rel="noopener noreferrer" className="bg-[#162c54] hover:bg-[#f37821] p-2 rounded-full transition-colors"><Instagram size={20} /></a>
              <a href="https://www.facebook.com/share/189XUrDh8D/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="bg-[#162c54] hover:bg-[#f37821] p-2 rounded-full transition-colors"><Facebook size={20} /></a>
            </div>
          </div>

          <div>
            <h4 className="text-white font-bold text-lg mb-6">{t.sitemap}</h4>
            <ul className="space-y-3 text-blue-200">
              <li><a href="#home" className="hover:text-white transition-colors">{t.home}</a></li>
              <li><a href="#services" className="hover:text-white transition-colors">{t.services}</a></li>
              <li><a href="#team" className="hover:text-white transition-colors">{t.team}</a></li>
              <li><a href="#gallery" className="hover:text-white transition-colors">{t.gallery}</a></li>
              <li><a href="#contact" className="hover:text-white transition-colors">{t.contact}</a></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold text-lg mb-6">{t.contactTitle}</h4>
            <div className="space-y-3 text-blue-200">
              <p className="flex items-start gap-2"><MapPin size={18} className="shrink-0 mt-1" /> 1949 N 400 E St, Ogden, UT 84414</p>
              <p className="flex items-center gap-2 font-bold text-[#f37821] text-lg mt-4"><Phone size={18} /> (385) 326-9168</p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 pt-8 border-t border-[#162c54] text-center text-sm text-blue-300 flex flex-col items-center">
          <span>&copy; {new Date().getFullYear()} ESO woof spa. {t.rights}</span>
        </div>
      </footer>

    </div>
  );
}

// --- BOOKING (DIRECT CONTACT) COMPONENT ---
function BookingForm({ t }) {
  return (
    <div className="flex flex-col items-center justify-center text-center h-full flex-1 gap-6 py-6 animate-[fadeIn_0.5s_ease-out]">
      <div className="w-20 h-20 bg-[#f37821]/10 rounded-full flex items-center justify-center">
        <Calendar className="text-[#f37821]" size={40} />
      </div>
      <p className="text-gray-600 max-w-sm text-lg font-medium">{t.contactPrompt}</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full max-w-md">
        <a href="tel:+13853269168" className="flex items-center justify-center gap-2 bg-[#1e3b70] hover:scale-105 text-white font-bold py-4 px-4 rounded-xl shadow-md transition-all">
          <Phone size={18} /> {t.contactCall}
        </a>
        <a href="sms:+13853269168" className="flex items-center justify-center gap-2 bg-[#f37821] hover:scale-105 text-white font-bold py-4 px-4 rounded-xl shadow-md transition-all">
          <MessageSquare size={18} /> {t.contactSMS}
        </a>
        <a href="mailto:contact@esowoofspa.com" className="flex items-center justify-center gap-2 bg-[#542468] hover:scale-105 text-white font-bold py-4 px-4 rounded-xl shadow-md transition-all">
          <Mail size={18} /> {t.contactEmail}
        </a>
      </div>
    </div>
  );
}

// --- SUBCOMPONENTS ---

function ServiceCard({ icon, title, desc, delay }) {
  const [ref, isVisible] = useScrollReveal();
  return (
    <div ref={ref} style={{ transitionDelay: `${delay}ms` }} className={`bg-white rounded-2xl p-8 shadow-md hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 border border-gray-100 group ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
      <div className="bg-gray-50 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#f37821]/10 transition-colors">
        {icon}
      </div>
      <h3 className="text-2xl font-bold text-[#1e3b70] mb-3">{title}</h3>
      <p className="text-gray-600 leading-relaxed">{desc}</p>
    </div>
  );
}

function TeamMember({ name, role, img }) {
  const [ref, isVisible] = useScrollReveal();
  return (
    <div ref={ref} className={`bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-700 border border-gray-100 group ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
      <div className="h-96 overflow-hidden relative">
        <img src={img} alt={name} className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700" />

        {/* Social Overlay on Hover */}
        <div className="absolute inset-0 bg-[#1e3b70]/60 flex items-center justify-center gap-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300 backdrop-blur-[2px]">
          <a href="https://www.instagram.com/eso.woofspa" target="_blank" rel="noopener noreferrer" className="bg-[#f37821] p-3 rounded-full text-white hover:scale-110 hover:bg-[#8e2634] transition-all">
            <Instagram size={28} />
          </a>
          <a href="https://www.facebook.com/share/189XUrDh8D/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="bg-[#f37821] p-3 rounded-full text-white hover:scale-110 hover:bg-[#8e2634] transition-all">
            <Facebook size={28} />
          </a>
        </div>
      </div>
      <div className="p-6 text-center">
        <h3 className="text-2xl font-bold text-[#1e3b70] mb-1">{name}</h3>
        <p className="font-bold text-[#f37821]">{role}</p>
      </div>
    </div>
  );
}

function TestimonialCarousel({ t }) {
  const [active, setActive] = useState(0);
  const reviews = t.reviews || [];

  useEffect(() => {
    if (!reviews.length) return;
    const interval = setInterval(() => {
      setActive((prev) => (prev + 1) % reviews.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [reviews.length]);

  const next = () => setActive((prev) => (prev + 1) % reviews.length);
  const prev = () => setActive((prev) => (prev - 1 + reviews.length) % reviews.length);

  if (!reviews.length) return null;

  return (
    <section className="py-24 bg-gray-50 text-gray-900 relative overflow-hidden border-y border-gray-200">
      <div className="max-w-4xl mx-auto px-4 relative z-10 text-center">
        <h2 className={`text-4xl md:text-5xl font-black ${BRAND_GRADIENT} bg-clip-text text-transparent mb-4 pb-2`}>
          {t.testTitle}
        </h2>
        <p className="text-[#1e3b70] mb-16 text-lg font-medium">{t.testSub}</p>

        <div className="relative bg-white p-10 md:p-16 rounded-3xl shadow-xl border border-gray-100">
          <QuoteIcon className="absolute top-6 left-6 text-[#f37821]/20 w-16 h-16" />

          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-700 ease-in-out"
              style={{ transform: `translateX(-${active * 100}%)` }}
            >
              {reviews.map((review, idx) => (
                <div key={idx} className="w-full flex-shrink-0 px-4">
                  <div className="min-h-[150px] flex items-center justify-center">
                    <p className="text-xl md:text-2xl font-medium leading-relaxed italic text-gray-700">
                      "{review.text}"
                    </p>
                  </div>
                  <div className="mt-8 flex flex-col items-center">
                    <div className="flex gap-1 text-[#f37821] mb-3">
                      {[1, 2, 3, 4, 5].map(i => <Star key={i} size={20} fill="currentColor" />)}
                    </div>
                    <p className="font-bold text-lg text-[#1e3b70]">
                      {review.author} <span className="font-normal text-gray-500 text-sm ml-2">({t.ownerOf} {review.pet})</span>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 flex justify-between px-2 md:-mx-6">
            <button onClick={prev} className="bg-white border border-gray-100 hover:bg-gray-50 p-3 rounded-full shadow-lg transition-colors text-[#1e3b70]">
              <ChevronLeft size={24} />
            </button>
            <button onClick={next} className="bg-white border border-gray-100 hover:bg-gray-50 p-3 rounded-full shadow-lg transition-colors text-[#1e3b70]">
              <ChevronRight size={24} />
            </button>
          </div>
        </div>

        <div className="flex justify-center gap-2 mt-8">
          {reviews.map((_, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`w-3 h-3 rounded-full transition-all ${active === i ? 'bg-[#f37821] w-8' : 'bg-[#1e3b70]/30 hover:bg-[#1e3b70]/60'}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function QuoteIcon(props) {
  return (
    <svg {...props} fill="currentColor" viewBox="0 0 24 24">
      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
    </svg>
  );
}
